import React, { useState, useEffect, Fragment } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Container,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Link,
    Chip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    AttachMoney,
    Receipt as ReceiptIconMui,
    ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportAPI } from '../../services/reportAPI';
import { ReportChart } from './ReportChart';
import { formatDateForInput, formatDateTime } from '../../utils/dateUtils';

interface ShopOrderRow {
    orderId: string;
    orderNumber: string;
    createdAt: string;
    paymentMethod: string;
    subtotal: number;
    discountAmount: number;
    tax: number;
    total: number;
    goodsSold: number;
}

interface ShopOrderDetailItem {
    productId: string;
    name: string;
    price: number;
    quantity: number;
    itemDiscountType?: 'percentage' | 'fixed' | null;
    itemDiscountValue?: number | null;
    itemDiscountAmount?: number | null;
}

// Subset of the full Order row (GET /shop/orders/:id, proxied to new-ateevo-pos) that the
// drill-down modal renders.
interface ShopOrderDetail {
    id: string;
    items: ShopOrderDetailItem[];
    subtotal: number;
    discountAmount: number;
    tax: number;
    total: number;
    paymentMethod: string;
    createdAt: string;
    completedAt?: string | null;
    cashTendered?: number | null;
    changeGiven?: number | null;
}

interface ShopReportBucket {
    periodStart: string;
    periodEnd: string;
    goodsSold: number;
    tax: number;
    orderCount: number;
}

interface ShopPaymentMethodBreakdown {
    method: string;
    goodsSold: number;
    tax: number;
    /** goods + tax (older Shop POS versions don't send it) */
    total?: number;
    orderCount: number;
}

interface ShopFinancialReportData {
    startDate: string;
    endDate: string;
    granularity: 'day' | 'week' | 'month';
    totals: {
        goodsSold: number;
        tax: number;
        orderCount: number;
    };
    byPaymentMethod: ShopPaymentMethodBreakdown[];
    buckets: ShopReportBucket[];
    orders: ShopOrderRow[];
    truncated: boolean;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
    helcim: 'Card (Terminal)',
    cash: 'Cash',
    member_account: 'Member Account (card on file)'
};

const PAYMENT_METHOD_COLORS: Record<string, string> = {
    helcim: '#2196F3',
    cash: '#4CAF50',
    member_account: '#FF9800'
};

const paymentMethodLabel = (method: string): string => PAYMENT_METHOD_LABELS[method] || method;

const escapeCSVField = (field: any): string => {
    if (field === null || field === undefined) return '""';
    const stringField = String(field);
    return `"${stringField.replace(/"/g, '""')}"`;
};

export const ShopFinancialReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reportData, setReportData] = useState<ShopFinancialReportData | null>(null);
    const [showTransactions, setShowTransactions] = useState(false);

    // Order drill-down modal
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [orderDetail, setOrderDetail] = useState<ShopOrderDetail | null>(null);
    const [orderDetailLoading, setOrderDetailLoading] = useState(false);
    const [orderDetailError, setOrderDetailError] = useState<string | null>(null);

    // Initialize date range to current month
    const now = new Date();
    const [startDate, setStartDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));

    useEffect(() => {
        loadReportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    const loadReportData = async () => {
        try {
            setLoading(true);
            setError(null);

            const startDateStr = formatDateForInput(startDate);
            const endDateStr = formatDateForInput(endDate);

            const data = await reportAPI.getShopFinancialReport({ startDate: startDateStr, endDate: endDateStr });
            setReportData(data);
        } catch (err) {
            setError('Failed to load Shop financial report data');
            console.error('Error loading Shop financial report:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenOrder = async (orderId: string) => {
        setOrderModalOpen(true);
        setOrderDetail(null);
        setOrderDetailError(null);
        setOrderDetailLoading(true);
        try {
            const data = await reportAPI.getShopOrder(orderId);
            setOrderDetail(data);
        } catch (err) {
            setOrderDetailError('Failed to load order details');
            console.error('Error loading Shop order detail:', err);
        } finally {
            setOrderDetailLoading(false);
        }
    };

    const handleCloseOrderModal = () => {
        setOrderModalOpen(false);
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatBucketLabel = (bucket: ShopReportBucket): string => {
        if (!reportData) return bucket.periodStart;
        if (reportData.granularity === 'day') return bucket.periodStart;
        return `${bucket.periodStart} – ${bucket.periodEnd}`;
    };

    const exportToCSV = () => {
        if (!reportData || reportData.orders.length === 0) return;

        const headers = ['Order #', 'Date', 'Payment Method', 'Subtotal', 'Discount', 'Tax', 'Total', 'Goods Sold (net)'];
        const rows = reportData.orders.map(o => [
            escapeCSVField(o.orderNumber),
            escapeCSVField(formatDateTime(o.createdAt)),
            escapeCSVField(paymentMethodLabel(o.paymentMethod)),
            escapeCSVField(o.subtotal.toFixed(2)),
            escapeCSVField(o.discountAmount.toFixed(2)),
            escapeCSVField(o.tax.toFixed(2)),
            escapeCSVField(o.total.toFixed(2)),
            escapeCSVField(o.goodsSold.toFixed(2))
        ].join(','));

        const csvContent = [headers.map(h => escapeCSVField(h)).join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `shop-financial-report-${reportData.startDate}-to-${reportData.endDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (loading && !reportData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!reportData) {
        return null;
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">Shop Financial Report</Typography>
            </Breadcrumbs>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                        <Typography variant="h4">Shop Financial Report</Typography>
                        <Box display="flex" gap={2} alignItems="center">
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => newValue && setStartDate(newValue)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => newValue && setEndDate(newValue)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                        </Box>
                    </Box>

                    {/* Key Metrics Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <AttachMoney sx={{ mr: 1, color: 'success.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Goods Sold
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(reportData.totals.goodsSold)}
                                    </Typography>
                                    <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                        Net of discounts · {reportData.totals.orderCount} orders
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <ReceiptIconMui sx={{ mr: 1, color: 'info.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Taxes Collected
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(reportData.totals.tax)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <ShoppingCartIcon sx={{ mr: 1, color: 'warning.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Orders
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {reportData.totals.orderCount}
                                    </Typography>
                                    <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                        Completed orders in range
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* Trend chart */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                    Trend by {reportData.granularity === 'day' ? 'Day' : reportData.granularity === 'week' ? 'Week' : 'Month'}
                                </Typography>
                                {reportData.buckets.length > 0 ? (
                                    <ReportChart
                                        type="bar"
                                        data={{
                                            labels: reportData.buckets.map(formatBucketLabel),
                                            datasets: [
                                                {
                                                    label: 'Goods Sold',
                                                    data: reportData.buckets.map(b => b.goodsSold),
                                                    backgroundColor: '#4CAF50'
                                                },
                                                {
                                                    label: 'Tax',
                                                    data: reportData.buckets.map(b => b.tax),
                                                    backgroundColor: '#2196F3'
                                                }
                                            ]
                                        }}
                                        height={320}
                                        options={{
                                            scales: {
                                                y: { beginAtZero: true }
                                            },
                                            plugins: {
                                                tooltip: {
                                                    callbacks: {
                                                        label: function (context: any) {
                                                            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                ) : (
                                    <Typography color="textSecondary" variant="body2">
                                        No completed Shop orders in this date range
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>

                        {/* Payment method breakdown */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                    By Payment Method
                                </Typography>
                                {reportData.byPaymentMethod.length > 0 ? (
                                    <>
                                        <ReportChart
                                            type="doughnut"
                                            data={{
                                                labels: reportData.byPaymentMethod.map(b => paymentMethodLabel(b.method)),
                                                datasets: [{
                                                    data: reportData.byPaymentMethod.map(b => b.goodsSold),
                                                    backgroundColor: reportData.byPaymentMethod.map(b => PAYMENT_METHOD_COLORS[b.method] || '#9E9E9E')
                                                }]
                                            }}
                                            height={220}
                                            options={{
                                                plugins: {
                                                    legend: { display: false },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: function (context: any) {
                                                                const b = reportData.byPaymentMethod[context.dataIndex];
                                                                return [
                                                                    `${paymentMethodLabel(b.method)}`,
                                                                    `Goods Sold: ${formatCurrency(b.goodsSold)}`,
                                                                    `Orders: ${b.orderCount}`
                                                                ];
                                                            }
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                        <Box display="flex" flexDirection="column" gap={1} sx={{ mt: 2 }}>
                                            {reportData.byPaymentMethod.map(b => (
                                                <Box key={b.method} display="flex" justifyContent="space-between" alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        label={paymentMethodLabel(b.method)}
                                                        sx={{
                                                            backgroundColor: `${PAYMENT_METHOD_COLORS[b.method] || '#9E9E9E'}22`,
                                                            color: PAYMENT_METHOD_COLORS[b.method] || '#616161',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                    <Typography variant="body2">
                                                        {formatCurrency(b.goodsSold)} · {b.orderCount} order{b.orderCount === 1 ? '' : 's'}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </>
                                ) : (
                                    <Typography color="textSecondary" variant="body2">
                                        No completed Shop orders in this date range
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Transactions */}
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                            <Typography variant="h6">
                                Transactions ({reportData.totals.orderCount})
                            </Typography>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={showTransactions ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    onClick={() => setShowTransactions(v => !v)}
                                    disabled={reportData.orders.length === 0}
                                >
                                    {showTransactions ? 'Hide Transactions' : 'View Transactions'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={exportToCSV}
                                    disabled={reportData.orders.length === 0}
                                >
                                    Export CSV
                                </Button>
                            </Box>
                        </Box>

                        {reportData.truncated && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                This range has more than 5,000 completed orders — showing (and exporting) only the first 5,000.
                                Narrow the date range to see the rest.
                            </Alert>
                        )}

                        {showTransactions && (
                            <TableContainer sx={{ mt: 2, maxHeight: 500 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Order #</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Payment Method</TableCell>
                                            <TableCell align="right">Subtotal</TableCell>
                                            <TableCell align="right">Discount</TableCell>
                                            <TableCell align="right">Tax</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.orders.map(o => (
                                            <TableRow key={o.orderId} hover>
                                                <TableCell>
                                                    <Link
                                                        component="button"
                                                        type="button"
                                                        variant="body2"
                                                        underline="hover"
                                                        onClick={() => handleOpenOrder(o.orderId)}
                                                    >
                                                        {o.orderNumber}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>{formatDateTime(o.createdAt)}</TableCell>
                                                <TableCell>{paymentMethodLabel(o.paymentMethod)}</TableCell>
                                                <TableCell align="right">{formatCurrency(o.subtotal)}</TableCell>
                                                <TableCell align="right">
                                                    {o.discountAmount > 0 ? `-${formatCurrency(o.discountAmount)}` : '-'}
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(o.tax)}</TableCell>
                                                <TableCell align="right">{formatCurrency(o.total)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Box>

                {/* Order detail modal */}
                <Dialog open={orderModalOpen} onClose={handleCloseOrderModal} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {orderDetail ? `Order #${orderDetail.id.slice(-8).toUpperCase()}` : 'Order Details'}
                    </DialogTitle>
                    <DialogContent dividers>
                        {orderDetailLoading && (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {orderDetailError && (
                            <Alert severity="error">{orderDetailError}</Alert>
                        )}

                        {orderDetail && !orderDetailLoading && (
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {formatDateTime(String(orderDetail.completedAt || orderDetail.createdAt))}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={paymentMethodLabel(orderDetail.paymentMethod)}
                                        sx={{
                                            backgroundColor: `${PAYMENT_METHOD_COLORS[orderDetail.paymentMethod] || '#9E9E9E'}22`,
                                            color: PAYMENT_METHOD_COLORS[orderDetail.paymentMethod] || '#616161',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>

                                {orderDetail.items.length === 0 ? (
                                    <Typography color="textSecondary" variant="body2">
                                        No items on this order.
                                    </Typography>
                                ) : (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Item</TableCell>
                                                <TableCell align="right">Qty</TableCell>
                                                <TableCell align="right">Price</TableCell>
                                                <TableCell align="right">Discount</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orderDetail.items.map((item, idx) => (
                                                <Fragment key={`${item.productId}-${idx}`}>
                                                    <TableRow>
                                                        <TableCell>{item.name}</TableCell>
                                                        <TableCell align="right">{item.quantity}</TableCell>
                                                        <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                                                        <TableCell align="right">
                                                            {item.itemDiscountAmount ? `-${formatCurrency(item.itemDiscountAmount)}` : '-'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatCurrency(item.price * item.quantity - (item.itemDiscountAmount || 0))}
                                                        </TableCell>
                                                    </TableRow>
                                                </Fragment>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}

                                <Divider sx={{ my: 2 }} />

                                <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" color="textSecondary">Subtotal</Typography>
                                        <Typography variant="body2">{formatCurrency(orderDetail.subtotal)}</Typography>
                                    </Box>
                                    {orderDetail.discountAmount > 0 && (
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body2" color="textSecondary">Discount</Typography>
                                            <Typography variant="body2">-{formatCurrency(orderDetail.discountAmount)}</Typography>
                                        </Box>
                                    )}
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" color="textSecondary">Tax</Typography>
                                        <Typography variant="body2">{formatCurrency(orderDetail.tax)}</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="subtitle2">Total</Typography>
                                        <Typography variant="subtitle2">{formatCurrency(orderDetail.total)}</Typography>
                                    </Box>
                                    {orderDetail.paymentMethod === 'cash' && orderDetail.cashTendered != null && (
                                        <>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2" color="textSecondary">Cash Tendered</Typography>
                                                <Typography variant="body2">{formatCurrency(orderDetail.cashTendered)}</Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2" color="textSecondary">Change Given</Typography>
                                                <Typography variant="body2">{formatCurrency(orderDetail.changeGiven || 0)}</Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseOrderModal}>Close</Button>
                    </DialogActions>
                </Dialog>
            </LocalizationProvider>
        </Container>
    );
};
